
import { useState, type FormEvent, type ReactNode } from 'react';
import { ToastFn, ZohoInfo } from '../../../types/integrations';
import { CopyRow } from '../../../components/common/StatusPillAndCopyRow';


interface StepsProps {
  children: ReactNode;
}

function Steps({ children }: StepsProps) {
  return (
    <ol className="list-decimal list-inside space-y-1.5 text-sm text-slate-600 [&_b]:text-slate-800 [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:rounded [&_code]:text-xs">
      {children}
    </ol>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

interface ConnectTabProps {
  info: ZohoInfo;
  onDone?: () => void;
  toast: ToastFn;
}

interface ConnectForm {
  dataCentre: string;
  clientId?: string;
  clientSecret?: string;
}

export default function ConnectTab({ info, onDone, toast }: ConnectTabProps) {
  const [form, setForm] = useState<ConnectForm>({ dataCentre: 'in' });
  const [busy, setBusy] = useState(false);

  const set =
    (k: keyof ConnectForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    try {
      // const r = await api.connectZoho(form.dataCentre, form.clientId, form.clientSecret, info.redirectUri);
      // window.location.href = r.authUrl;
      toast('Redirecting to Zoho…');
      onDone?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to connect', 'bad');
    } finally {
      setBusy(false);
    }
  };

  const connected = info.status === 'CONNECTED';

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-slate-900">
          {connected ? 'Update connection' : 'Connect'}
        </h2>

        <Steps>
          <li>
            Open the{' '}
            <a
              href="https://api-console.zoho.in"
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              Zoho API Console
            </a>{' '}
            (use your region's domain) and add a{' '}
            <b>Server-based Application</b>.
          </li>
          <li>
            Homepage URL: your website. Authorized Redirect URI:{' '}
            <code className="break-all">{info.redirectUri}</code>
          </li>
          <li>
            Copy the Client ID and Client Secret here and click Connect. Zoho
            asks you to allow access.
          </li>
        </Steps>

        <form className="flex flex-col gap-4" onSubmit={submit}>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Data centre
            </span>
            <select
              value={form.dataCentre}
              onChange={set('dataCentre')}
              className={inputCls}
            >
              {(info.dataCentres ?? ['in', 'com', 'eu']).map((d) => (
                <option key={d} value={d}>
                  zoho.{d}
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              Where your Zoho account lives (zoho.in, zoho.com …)
            </span>
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Client ID</span>
            <input
              required
              value={form.clientId ?? ''}
              onChange={set('clientId')}
              className={inputCls}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">
              Client Secret
            </span>
            <input
              required
              type="password"
              autoComplete="off"
              value={form.clientSecret ?? ''}
              onChange={set('clientSecret')}
              className={inputCls}
            />
          </label>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? 'Connecting…' : connected ? 'Reconnect' : 'Connect'}
            </button>
          </div>
        </form>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-4">
        <h2 className="text-lg font-semibold text-slate-900">Webhook</h2>

        {!info.setup ? (
          <p className="text-sm text-slate-600">
            Connect first. You will then get the webhook address for your Zoho
            workflow rules.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-slate-600">
              To welcome new leads on WhatsApp: Zoho CRM →{' '}
              <b>Setup → Automation → Workflow Rules</b> → new rule on{' '}
              <b>Leads</b> (and one on <b>Contacts</b>) "On create" → action{' '}
              <b>Webhook</b>:
            </p>
            <CopyRow
              label="URL to notify (method POST)"
              value={info.setup.webhookUrl}
              toast={toast}
            />
            <p className="text-xs text-slate-500">
              Body: <b>Raw (JSON)</b> or form fields with these names, mapped to
              the record's fields:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {info.setup.parameters.map((p) => (
                <code
                  key={p}
                  className="bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-700"
                >
                  {p}
                </code>
              ))}
            </div>
            <p className="text-xs text-slate-500">
              Set <code className="bg-slate-100 px-1 rounded">module</code> to
              "Leads" or "Contacts" and{' '}
              <code className="bg-slate-100 px-1 rounded">event</code> to
              "create".
            </p>
          </div>
        )}
      </section>
    </div>
  );
}